import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  FiUpload, FiCheck, FiX, FiRefreshCw, FiImage, FiMessageSquare,
  FiArrowLeft, FiZoomIn, FiDownload, FiClock, FiAlertCircle, FiChevronRight
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = getApiUrl();

const MAX_REVISIONS = 3;

const StatusBadge = ({ status }) => {
  const configs = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
    approved: { label: 'Approuvé', color: 'bg-green-100 text-green-800' },
    revision_requested: { label: 'Révision demandée', color: 'bg-orange-100 text-orange-800' },
    in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-800' },
  };
  const config = configs[status] || configs.pending;
  return <Badge className={config.color}>{config.label}</Badge>;
};

export default function DesignApprovalPage() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [design, setDesign] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  
  // Revision request state
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  
  // Image preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load reservation
      const resResponse = await fetch(`${API_URL}/api/reservations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resResponse.ok) {
        const reservations = await resResponse.json();
        const res = reservations.find(r => r.id === reservationId);
        setReservation(res);
      }
      
      // Load design
      const designResponse = await fetch(`${API_URL}/api/designs/${reservationId}`);
      if (designResponse.ok) {
        const data = await designResponse.json();
        if (data.exists) {
          setDesign(data.design);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [reservationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 5MB)');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUploadDesign = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    try {
      // First upload the image
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const uploadResponse = await fetch(`${API_URL}/api/designs/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }
      
      const uploadData = await uploadResponse.json();
      
      // Then create/update the design
      const designResponse = await fetch(`${API_URL}/api/designs/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservation_id: reservationId,
          image_url: uploadData.url,  // Store relative URL only
          notes: uploadNotes
        })
      });
      
      if (designResponse.ok) {
        const data = await designResponse.json();
        toast.success(data.message);
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setPreviewUrl(null);
        setUploadNotes('');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/designs/${reservationId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: reservation?.client_name || 'client' })
      });
      
      if (response.ok) {
        toast.success('Design approuvé avec succès !');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionFeedback.trim()) {
      toast.error('Veuillez décrire les modifications souhaitées');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/designs/${reservationId}/request-revision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: revisionFeedback })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setRevisionDialogOpen(false);
        setRevisionFeedback('');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la demande de révision');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    // If already absolute URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Convert relative URL to absolute
    return `${API_URL}${url}`;
  };

  const getCurrentDesign = () => {
    if (!design?.revisions?.length) return null;
    return design.revisions[design.revisions.length - 1];
  };

  const getRemainingRevisions = () => {
    return MAX_REVISIONS - (design?.revision_requests?.length || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Réservation non trouvée</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/reservations')}>
          <FiArrowLeft className="mr-2" /> Retour aux réservations
        </Button>
      </div>
    );
  }

  const currentDesign = getCurrentDesign();
  const remainingRevisions = getRemainingRevisions();
  const isApproved = design?.status === 'approved';
  const canRequestRevision = !isApproved && remainingRevisions > 0;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/reservations')}>
            <FiArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Approbation du Design</h1>
            <p className="text-slate-500">
              Réservation de {reservation.client_name} - {new Date(reservation.start_date).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
        {design && <StatusBadge status={design.status} />}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FiAlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Processus d'approbation</p>
            <p className="text-sm text-blue-600">
              Vous pouvez demander jusqu'à {MAX_REVISIONS} révisions gratuites. 
              Il vous reste <strong>{remainingRevisions} révision(s)</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Design */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FiImage className="h-5 w-5" />
                Design actuel
              </span>
              {design?.current_version && (
                <Badge variant="outline">Version {design.current_version}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentDesign ? (
              <div className="space-y-4">
                <div 
                  className="aspect-video bg-slate-100 rounded-lg overflow-hidden cursor-pointer relative group"
                  onClick={() => {
                    setSelectedImage(getImageUrl(currentDesign.image_url));
                    setPreviewDialogOpen(true);
                  }}
                >
                  <img
                    src={getImageUrl(currentDesign.image_url)}
                    alt={`Design v${currentDesign.version}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#f1f5f9" width="400" height="300"/><text x="200" y="140" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="14">Image non disponible</text><text x="200" y="165" text-anchor="middle" fill="#cbd5e1" font-family="sans-serif" font-size="12">Veuillez uploader une nouvelle version</text></svg>');
                    }}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <FiZoomIn className="h-8 w-8 text-white" />
                  </div>
                </div>
                
                {currentDesign.notes && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">{currentDesign.notes}</p>
                  </div>
                )}
                
                <p className="text-xs text-slate-500">
                  Uploadé le {new Date(currentDesign.created_at).toLocaleDateString('fr-FR')} à {new Date(currentDesign.created_at).toLocaleTimeString('fr-FR')}
                </p>

                {/* Action Buttons */}
                {!isApproved && (
                  <div className="flex gap-3">
                    <Button
                      onClick={handleApprove}
                      disabled={submitting}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <FiCheck className="mr-2" />
                      Approuver
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setRevisionDialogOpen(true)}
                      disabled={submitting || !canRequestRevision}
                      className="flex-1"
                    >
                      <FiRefreshCw className="mr-2" />
                      Demander révision
                    </Button>
                  </div>
                )}
                
                {isApproved && (
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <FiCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="font-medium text-green-800">Design approuvé !</p>
                    <p className="text-sm text-green-600">
                      Le {new Date(design.approved_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <FiImage className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Aucun design uploadé</p>
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <FiUpload className="mr-2" />
                  Uploader un design
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revision History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiClock className="h-5 w-5" />
              Historique
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Timeline */}
              {design?.revisions?.map((revision, index) => (
                <motion.div
                  key={revision.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-3"
                >
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === design.revisions.length - 1
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {revision.version}
                    </div>
                    {index < design.revisions.length - 1 && (
                      <div className="w-0.5 h-full bg-slate-200 my-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">Version {revision.version}</p>
                      <span className="text-xs text-slate-500">
                        {new Date(revision.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedImage(getImageUrl(revision.image_url));
                        setPreviewDialogOpen(true);
                      }}
                      className="text-sm text-indigo-600 hover:underline"
                    >
                      Voir le design
                    </button>
                    {revision.notes && (
                      <p className="text-sm text-slate-500 mt-1">{revision.notes}</p>
                    )}
                  </div>
                </motion.div>
              ))}
              
              {/* Revision Requests */}
              {design?.revision_requests?.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <FiMessageSquare className="h-4 w-4" />
                    Demandes de révision
                  </h4>
                  {design.revision_requests.map((req, index) => (
                    <div key={req.id} className="p-3 bg-orange-50 rounded-lg mb-2">
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-medium">Version {req.version}</p>
                        <span className="text-xs text-slate-500">
                          {new Date(req.requested_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{req.feedback}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {!design?.revisions?.length && (
                <p className="text-center text-slate-500 py-4">Aucun historique</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Button for Business */}
      {!isApproved && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Uploader une nouvelle version</p>
                <p className="text-sm text-slate-500">
                  Formats acceptés : JPG, PNG, WebP (max 5MB)
                </p>
              </div>
              <Button onClick={() => setUploadDialogOpen(true)}>
                <FiUpload className="mr-2" />
                Uploader
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uploader un design</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Image du design</Label>
              <div 
                className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 transition-colors ${
                  previewUrl ? 'border-indigo-400' : 'border-slate-200'
                }`}
                onClick={() => document.getElementById('design-upload').click()}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded" />
                ) : (
                  <>
                    <FiUpload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Cliquez pour sélectionner une image</p>
                    <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP (max 5MB)</p>
                  </>
                )}
              </div>
              <input
                id="design-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Description des modifications apportées..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleUploadDesign} disabled={uploading || !selectedFile}>
              {uploading ? (
                <>
                  <FiRefreshCw className="mr-2 animate-spin" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <FiUpload className="mr-2" />
                  Uploader
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision Request Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Demander une révision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>Attention :</strong> Il vous reste {remainingRevisions} révision(s) gratuite(s).
              </p>
            </div>
            
            <div>
              <Label>Décrivez les modifications souhaitées *</Label>
              <Textarea
                value={revisionFeedback}
                onChange={(e) => setRevisionFeedback(e.target.value)}
                placeholder="Ex: Pourriez-vous changer la couleur du fond en bleu et agrandir le texte..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleRequestRevision} 
              disabled={submitting || !revisionFeedback.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? (
                <>
                  <FiRefreshCw className="mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <FiRefreshCw className="mr-2" />
                  Demander révision
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Aperçu du design</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex justify-center">
              <img
                src={selectedImage}
                alt="Design preview"
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Fermer
            </Button>
            <Button asChild>
              <a href={selectedImage} download target="_blank" rel="noopener noreferrer">
                <FiDownload className="mr-2" />
                Télécharger
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
